import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const AppsSection = ({ onUpdate, boostInput = false }) => {
  const { fetchCachedList } = useBuild();
  const [currentApp, setCurrentApp] = useState('');
  const [boost, setBoost] = useState(1);
  const [appOptions, setAppOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch app options on component mount
  useEffect(() => {
    const fetchAppOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const appsData = await fetchCachedList('/search/frequent_values/farcaster-items/app');
        setAppOptions(appsData);
      } catch (err) {
        console.error('Failed to fetch app options:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAppOptions();
  }, [fetchCachedList]);

  const handleAppAction = (actionType) => {
    if (!currentApp) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: 'app',
      value: [""+parseInt(currentApp)]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    setCurrentApp(''); // Reset after adding
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">
          Failed to load apps: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>App</InputLabel>
        <Select
          value={currentApp}
          label="App"
          onChange={(e) => setCurrentApp(e.target.value)}
          disabled={loading}
        >
          {appOptions.map((app) => (
            <MenuItem key={app.id} value={app.id}>
              {""+parseInt(app.id)} ({app.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
          disabled={loading}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleAppAction('include')}
          disabled={!currentApp || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleAppAction('boost')}
            disabled={!currentApp || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleAppAction('exclude')}
          disabled={!currentApp || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default AppsSection;

